import {ChangeDetectionStrategy, Component, effect, Injectable, input, output} from '@angular/core';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from "@angular/forms";
import {MatButtonModule} from "@angular/material/button";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {TranslateModule} from "@ngx-translate/core";
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from "@angular/cdk/drag-drop";
import {GruppeId, HtmlTekst, TemaId, TemaModell, TemaRessursClient, TemaSpesifikasjon} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {StandardValideringsfeilPipe} from "felles-ng2/pipes/StandardValideringsfeilPipe";
import {MatIcon} from "@angular/material/icon";
import {KortHandlingerKomponent} from "web-ng2/delt/komponenter/kort/kort-handlinger/kort-handlinger";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {håndterRedigerListeEndringer} from "felles-ng2/infrastruktur/angular/redigerListeSkjema";

export type TemaSkjema = ReturnType<typeof GruppeTemaRedigerListeKomponent.opprettSkjemarad>
export type TemaSkjemaVerdier = TemaSkjema['value']

@Component({
    selector: 'gruppe-tema-rediger-liste',
    templateUrl: './gruppe-tema-rediger-liste.html',
    styleUrls: ['./gruppe-tema-rediger-liste.scss'],
    imports: [
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatTooltipModule,
        MatCheckboxModule,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        TranslateModule,
        StandardValideringsfeilPipe,
        MatIcon,
        KortHandlingerKomponent,
        RiktekstRedigeringKomponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GruppeTemaRedigerListeKomponent {

    /** Kalles når bruker har lagret eller avbryter, returnerer oppdaterte temaer ved lagret, null ved avbrutt */
    vedFerdig = output<TemaModell[] | null>();

    temaer = input.required<TemaModell[]>();
    gruppeId = input.required<GruppeId>();

    skjema = new FormArray<TemaSkjema>([]);
    lagrer = asynkArbeidPågårSignal();

    static opprettSkjemarad(verdier = {id: null as TemaId | null, tittel: '', beskrivelse: '' as HtmlTekst, arkivert: false, ny: true, kanIkkeSlettes: false, harForelder: false}) {
        const rad = new FormGroup({
            id: new FormControl<TemaId | null>(verdier.id),
            tittel: new FormControl<string>(verdier.tittel, {nonNullable: true, validators: FeltValidatorer.litenStreng}),
            beskrivelse: new FormControl<string>(verdier.beskrivelse, {nonNullable: true}),
            arkivert: new FormControl<boolean>(verdier.arkivert, {nonNullable: true}),
            slettet: new FormControl<boolean>(false, {nonNullable: true}),
            kanIkkeSlettes: new FormControl<boolean>(verdier.kanIkkeSlettes, {nonNullable: true}),
            erArvet: new FormControl<boolean>(verdier.harForelder, {nonNullable: true}),
            ny: new FormControl<boolean>(verdier.ny, {nonNullable: true})
        });
        if (verdier.arkivert || verdier.harForelder) rad.disable({emitEvent: false});
        return rad;
    }

    constructor(private avhengigheter: GruppeTemaRedigerListeKomponentAvhengigheter) {
        this.skjema.valueChanges.pipe(takeUntilDestroyed())
          .subscribe(() => håndterRedigerListeEndringer(this.skjema, GruppeTemaRedigerListeKomponent.opprettSkjemarad));

        effect(() => {
            this.skjema.clear();
            this.temaer().forEach(tema =>
              this.skjema.push(GruppeTemaRedigerListeKomponent.opprettSkjemarad({...tema, tittel: tema.navn, ny: false}), {emitEvent: false}));
            //sørger for at det blir lagt til en tom 'nytt innslag' rad
            håndterRedigerListeEndringer(this.skjema, GruppeTemaRedigerListeKomponent.opprettSkjemarad);
        });
    }

    async lagre() {
        const oppdaterteTemaer = await this.lagrer.utfør(() =>
          this.avhengigheter.oppdaterTemaer(this.gruppeId(), this.somSpesifikasjon()));
        this.vedFerdig.emit(oppdaterteTemaer);
    }

    avbryt() {
        this.vedFerdig.emit(null);
    }

    slett(rad: TemaSkjema, indeks: number) {
        if (rad.value.ny) {
            this.skjema.removeAt(indeks);
        } else {
            rad.disable();
            rad.patchValue({slettet: true});
        }
    }

    angreSlett(rad: TemaSkjema) {
        rad.enable();
        rad.patchValue({slettet: false});
    }

    arkiver(rad: TemaSkjema) {
        rad.disable();
        rad.patchValue({arkivert: true});
    }

    reaktiver(rad: TemaSkjema) {
        rad.patchValue({arkivert: false});
        rad.enable();
    }

    flyttingTillatFn() {
        return (_indeks: number, _flyttetRad: CdkDrag<number>) => {
            return _indeks < this.skjema.controls.length - 1;
        };
    }

    radFlyttet(event: CdkDragDrop<any>) {
        const rad = this.skjema.at(event.previousIndex);
        this.skjema.removeAt(event.previousIndex);
        this.skjema.insert(event.currentIndex, rad);
    }

    private somSpesifikasjon(): TemaSpesifikasjon[] {
        return this.skjema.getRawValue()
          .filter(rad => !rad.slettet && !erTomEllerNil(rad.tittel))
          .map(rad => ({
              id: rad.id as unknown as TemaId,
              navn: rad.tittel!,
              beskrivelse: rad.beskrivelse! as HtmlTekst,
              arkivert: rad.arkivert!
          }));
    }
}

@Injectable()
export class GruppeTemaRedigerListeKomponentAvhengigheter {

    constructor(private temaRessurs: TemaRessursClient) {
    }

    oppdaterTemaer(gruppeId: GruppeId, temaer: TemaSpesifikasjon[]): Promise<TemaModell[]> {
        return this.temaRessurs.oppdater({gruppeId, temaer}).somPromise();
    }
}
